import type { CateringWidgetTheme } from '@/components/catering-widget';

// Named design-token bundles for the embedded widget. A venue selects a
// preset via the `?theme=<name>` query param on /embed/{venueId}. When
// omitted (or set to 'default'), the widget renders in BookOne's
// burgundy/gold defaults. Branded presets are hand-tuned per venue — add
// an entry here, no code changes required. Hand off the built-in
// DEFAULT_THEME fallback chain by returning Partial<> and letting the
// widget merge with its defaults.
//
// Why not user-editable theme tokens via a form? Because tuning a widget
// skin to read against a venue's fonts + colors + negative space is design
// work, not form-filling. Doing it in code keeps the delta reviewable and
// avoids a half-baked editor UI that produces unusable skins. One
// afternoon per venue, hardcoded, shipped.
export type BrandPreset = 'default' | 'akiro' | 'ottavio' | 'membersonly' | 'boxcar' | 'andson' | 'mala' | 'clandestino' | 'kayao' | 'ayayay' | 'matilda' | 'exodo' | 'delancey' | 'trattoria' | 'coast' | 'glowbal' | 'italian-kitchen' | 'five-sails' | 'black-and-blue' | 'the-roof' | 'rileys' | 'baccala' | 'lebuffet';

export const BRAND_PRESETS: Record<BrandPreset, Partial<CateringWidgetTheme>> = {
  // Empty = widget uses DEFAULT_THEME (BookOne burgundy/gold).
  default: {},

  // Akiro Hand Roll Bar — minimalist-luxe serif on cream. Matches the
  // aesthetic register of akironikkei.com (dark charcoal copy on a warm
  // off-white ground; serif display for titles; quiet, generous spacing).
  // We use Instrument Serif (already loaded in the root layout for /journal
  // and /history) instead of Akiro's paid brand font — close enough register
  // without the licensing lift. Swap to the real font in Phase 2.
  akiro: {
    // Dark monochrome stack matching akironikkei.com. Panel is near-black
    // like the page, transcript sits a shade lighter for contrast, agent
    // bubbles are cream to echo the form inputs on the real events page.
    // No burgundy leakage — every hardcoded surface token is overridden.
    backgroundColor: '#0A0A0A',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    primaryColor: '#F5F0E6',
    accentColor: '#D0C5B4',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#F5F0E6',
    textSecondary: 'rgba(245, 240, 230, 0.62)',
    surfacePanel: '#111111',
    agentBubbleBg: '#F5F0E6',
    agentBubbleText: '#1A1A1A',
    sendButtonDisabledBg: 'rgba(245, 240, 230, 0.10)',
  },

  // Ottavio — Italian pizza/pasta/mussels, 4 Quebec locations. Palette
  // drawn from the ottavio.ca logo: bright tomato red "O" + black script on
  // warm cream. Cream panel reads as an Italian table cloth; red as the
  // primary accent (buttons, focus). Italic-friendly serif on the display
  // side echoes the cursive "ttavio" wordmark without needing the licensed
  // font. Wave palette stays BookOne burgundy/gold since red would clash
  // with the wave's bright gold highlight — the red lives in CTAs instead.
  ottavio: {
    backgroundColor: '#FBF7EF',
    borderColor: 'rgba(26, 5, 5, 0.12)',
    primaryColor: '#D9322F',
    accentColor: '#8C1C17',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#1A0505',
    textSecondary: 'rgba(26, 5, 5, 0.62)',
    surfacePanel: '#FFFFFF',
    agentBubbleBg: '#FFF5E6',
    agentBubbleText: '#1A0505',
    // User bubble text on the red gradient — white reads better than
    // the default near-black against #D9322F → #8C1C17.
    userBubbleText: '#FFFFFF',
    // Light overlay for the "No audio?" fallback so Ottavio's dark
    // transcript text stays legible. Matches the panel background.
    audioFallbackBg: 'rgba(251, 247, 239, 0.96)',
    // Ottavio's logo is a wide wordmark (369x142 native). Circle crop
    // chops it to unreadable middle letters; render the full wordmark
    // at a modest height instead.
    logoDisplayMode: 'wide',
    // Italian tricolor palette — green + white + red. Pairs with
    // `ottavioLight` at listening/speaking swap time.
    wavePalette: 'ottavio',
    sendButtonDisabledBg: 'rgba(26, 5, 5, 0.10)',
  },

  // Le Buffet des Continents — all-you-can-eat international buffet, 7 Quebec
  // locations. Their only logo is a WHITE wordmark ("blanc"), so the whole card
  // is ORANGE-FORWARD (brand orange #e7791c, from --firstColor on their site) and
  // the white wordmark reads on it. IMPORTANT: the catering-widget paints every
  // surface with `surfacePanel` (it never reads `backgroundColor`), so surfacePanel
  // IS the card — it must be the orange, and the body text must be DARK (near-black
  // brown #2b1a06, ~6:1 on orange) so the transcript + what you type stay legible.
  // (The prior white surfacePanel + white textPrimary made the chat text invisible.)
  // Deep brown (#5c3b00 → #3a2600) is the CTA/user-bubble gradient; cream agent
  // bubbles carry dark text. logoDisplayMode 'wide' — wide wordmark.
  lebuffet: {
    backgroundColor: '#E7791C',
    borderColor: 'rgba(43, 26, 6, 0.22)',
    primaryColor: '#5C3B00',
    accentColor: '#3A2600',
    fontDisplay: '"Clash Display", "Open Sans Condensed", "Arial Narrow", sans-serif',
    fontBody: '"General Sans", "Open Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#2B1A06',
    textSecondary: 'rgba(43, 26, 6, 0.72)',
    surfacePanel: '#E7791C',
    agentBubbleBg: '#FFF6EC',
    agentBubbleText: '#3A2600',
    userBubbleText: '#FFFFFF',
    audioFallbackBg: 'rgba(231, 121, 28, 0.97)',
    logoDisplayMode: 'wide',
    wavePalette: 'bookone',
    sendButtonDisabledBg: 'rgba(255, 255, 255, 0.24)',
  },

  // Members Only West Village — iconic 1975-era NYC lounge. Monochrome black +
  // white with parchment-warm cream accents. Brand display face is Copperplate
  // Gothic Std (paid licence); Instrument Serif here lands in the same
  // institutional-serif register without the licensing lift. The wordmark
  // logo (white text on black) is centred in a 512×512 square so it crops
  // cleanly into the floating-widget circle trigger over a black bg.
  membersonly: {
    backgroundColor: '#0A0A0A',
    borderColor: 'rgba(245, 240, 230, 0.10)',
    primaryColor: '#F5F0E6',
    accentColor: '#C9BFA5',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#F5F0E6',
    textSecondary: 'rgba(245, 240, 230, 0.62)',
    surfacePanel: '#111111',
    agentBubbleBg: '#F5F0E6',
    agentBubbleText: '#0E0E0E',
    // "No audio?" overlay matches the panel background. Without this it
    // falls through to the default burgundy-tinted near-black, which leaks
    // BookOne's color into the otherwise pure-black Members Only theme.
    audioFallbackBg: '#0A0A0A',
    sendButtonDisabledBg: 'rgba(245, 240, 230, 0.10)',
  },

  // Boxcar Social Group — single shared preset for all 4 venues (Bobby
  // Bermuda's, Boxcar Harbourfront, Boxcar Summerhill, Boxcar Laneway).
  // Per-location accent variants are deferred to v1.5 polish; the v1 ship
  // velocity wins. Palette traced from boxcarsocial.ca's actual page CSS:
  // pure monochrome (only #000, #111, #262627, #363636, #878787, #f1f1f1,
  // #f7f7f7, #fff). No brick / burgundy / red accent on the live brand —
  // it's a grayscale / espresso bar register. Widget chrome uses #111111
  // panel + #f7f7f7 cream-white text + #262627 borders + #878787 subtle
  // text. Matches the dark-chrome Members Only structure but with Boxcar's
  // exact tokens.
  boxcar: {
    backgroundColor: '#111111',
    borderColor: 'rgba(247, 247, 247, 0.10)',
    primaryColor: '#F7F7F7',
    accentColor: '#878787',
    fontDisplay: 'Figtree, "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontBody: 'Figtree, "Helvetica Neue", Helvetica, Arial, sans-serif',
    textPrimary: '#F7F7F7',
    textSecondary: 'rgba(247, 247, 247, 0.62)',
    surfacePanel: '#262627',
    agentBubbleBg: '#F7F7F7',
    agentBubbleText: '#111111',
    audioFallbackBg: '#111111',
    sendButtonDisabledBg: 'rgba(247, 247, 247, 0.10)',
  },

  // & Son Steakeasy — modern steakhouse + speakeasy in a 190-year-old
  // Greenwich Village townhouse. Dark monochrome stack mirrors the Akiro /
  // Members Only register (near-black panel, cream agent bubbles) but the
  // accent color shifts to a warm foil gold (#C9A55C) drawn from the venue's
  // wordmark — gold-leaf lettering on aged paper. Display face stays
  // Instrument Serif (already loaded in root layout) for the same Old NYC
  // serif register without paying for the brand's licensed face.
  andson: {
    backgroundColor: '#0A0A0A',
    borderColor: 'rgba(201, 165, 92, 0.16)',
    primaryColor: '#F5EFE0',
    accentColor: '#C9A55C',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#F5EFE0',
    textSecondary: 'rgba(245, 239, 224, 0.62)',
    surfacePanel: '#111111',
    agentBubbleBg: '#F5EFE0',
    agentBubbleText: '#1A1208',
    audioFallbackBg: '#0A0A0A',
    sendButtonDisabledBg: 'rgba(245, 239, 224, 0.10)',
    // The two-line gold-foil wordmark (& SON / STEAKEASY with diamond
    // ornaments) gets clipped by the default 96–120px circular crop —
    // only "SO" and "EAKEA" survive. Wide mode renders it as a centered
    // rectangle (max 220×56 idle, 88×40 active) with objectFit:contain
    // so the full wordmark stays readable. Same pattern Ottavio uses.
    logoDisplayMode: 'wide',
  },

  // MáLà Project — bilingual EN/中文 Chinese-American restaurant with 4 NYC
  // locations (East Village, Bryant Park, Midtown East, Greenpoint). Brand
  // palette traced directly from malaproject.com/events via /browse color
  // sampling: bright yellow canvas (#FFE71E rgb 255,231,30) with purple
  // (#67287F rgb 103,40,127) "book now!" CTAs. The 4 location-specific hex
  // codes (pink/orange/purple/teal) layered into the wave palette so the
  // speaking visualizer cycles through every location's brand color plus
  // the events-page yellow anchor (no dark core stop — R42.1). Inter sans
  // matches the site's body typography. Pink circle "SERVING JOY SINCE
  // 2015" logo (mala-circle-logo.jpg) displays in widget header.
  //
  // R42.1 (2026-05-12) flipped the canvas from dark to yellow to match the
  // source. The user/agent bubble distinction is preserved on yellow without
  // a userBubbleBg override (theme type doesn't support one): user bubbles
  // render as a solid purple gradient pill on the right (primaryColor +
  // accentColor both = purple) with yellow text inside (userBubbleText),
  // while agent messages render as plain black text on the yellow surface
  // with no background bubble — high contrast either way.
  mala: {
    backgroundColor: '#FFE71E',
    borderColor: 'rgba(13, 13, 13, 0.20)',
    primaryColor: '#67287F',
    accentColor: '#67287F',
    fontDisplay: '"Inter", Helvetica, Arial, sans-serif',
    fontBody: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#000000',
    textSecondary: 'rgba(0, 0, 0, 0.62)',
    surfacePanel: '#FFE71E',
    agentBubbleBg: '#67287F',
    agentBubbleText: '#FFE71E',
    userBubbleText: '#FFE71E',
    audioFallbackBg: '#FFE71E',
    sendButtonDisabledBg: 'rgba(13, 13, 13, 0.10)',
    wavePalette: 'mala',
  },

  // Clandestino Cocktail Social Club — intimate speakeasy cocktail club hidden
  // beneath Matilda on Wells Street, Chicago River North. Brand born from "a
  // love of the hidden, the intimate, and the extraordinary" — underground
  // speakeasy spirit + artistic heart of Latin America. "It's where those
  // who know — know."
  //
  // Palette sampled from clandestinosocialclub.com via /browse: pure black
  // canvas (#000000) + a single blood-red accent (#CA1929 rgb 202,25,41) used
  // for headings, circular nav outlines, and the diagonal "COCKTAIL SOCIAL
  // CLUB" wordmark strike. Body text is white; secondary text uses a warm
  // cream (#F9F2E6) sampled from the same site. The brand uses "League"
  // for display (paid font); Instrument Serif (already loaded in root layout)
  // is a free-tier register-equivalent for the speakeasy mood.
  clandestino: {
    backgroundColor: '#000000',
    borderColor: 'rgba(202, 25, 41, 0.18)',
    primaryColor: '#CA1929',
    accentColor: '#CA1929',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: 'Helvetica, "Helvetica Neue", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(249, 242, 230, 0.62)',
    surfacePanel: '#0A0606',
    agentBubbleBg: 'rgba(202, 25, 41, 0.10)',
    agentBubbleText: '#FFFFFF',
    userBubbleText: '#FFFFFF',
    sendButtonDisabledBg: 'rgba(202, 25, 41, 0.18)',
    wavePalette: 'clandestino',
    // 'wide' mode renders the wordmark as a horizontal lockup (vs 'circle'
    // which crops to a disc). The "COCKTAIL SOCIAL CLUB" diagonal lockup
    // only reads with all three lines visible — same call Ottavio makes.
    logoDisplayMode: 'wide',
  },

  // Kayao Restaurant — Chifa-style Peruvian-Japanese-Chinese fusion at 1252
  // N Wells Street, Chicago. Brand born from El Puerto del Callao, Peru —
  // the port city that birthed the cuisine. Palette sampled from
  // kayaorestaurant.com via /browse:
  //   - background: deep teal #013334 (rgb 1,51,52)
  //   - accent: coral #FD6632 (rgb 253,102,50) — the fish color from the
  //     wordmark, used for all CTAs + nav buttons + section dividers
  //   - text primary: cream #E8E4D8 (rgb 232,228,216) — welcome-page title
  //
  // The brand uses a custom serif for the welcome line ("kayao, the new
  // culinary oasis..."). Substituting Instrument Serif (already loaded in
  // root layout) — close register without the licensing lift.
  kayao: {
    backgroundColor: '#013334',
    borderColor: 'rgba(253, 102, 50, 0.18)',
    primaryColor: '#FD6632',
    accentColor: '#FD6632',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: 'Helvetica, "Helvetica Neue", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#E8E4D8',
    textSecondary: 'rgba(232, 228, 216, 0.62)',
    surfacePanel: '#012829',
    agentBubbleBg: 'rgba(253, 102, 50, 0.10)',
    agentBubbleText: '#E8E4D8',
    userBubbleText: '#013334',  // deep-teal text on coral user-bubble pill
    sendButtonDisabledBg: 'rgba(253, 102, 50, 0.18)',
    wavePalette: 'kayao',
    // Wordmark is the kayao fish + "kayao" lockup — wide aspect ratio,
    // needs 'wide' to render fully (same as Clandestino + Ottavio + Akiro).
    logoDisplayMode: 'wide',
  },

  // Ayayay — Mexican-Peruvian eatery on Wells Street. Brand re-sampled from
  // the ayayaymex.com illustrated key art (cocktail + lime + chili scene):
  // soft salmon pink canvas (#F5A8A0) with deep forest green (#0F5A4D)
  // primary + bright coral (#D33C2A) accent + cream (#FAEDC7) highlight.
  // The voice is playful ("SPICY UP THE FUN!"), and the agent persona pulls
  // from Chef Jose Luis Chavez (Mexico City) + Chef Adolfo Gosalvez (Lima).
  // The previous dark-green-canvas variant read as Kayao-adjacent; this
  // pink-canvas variant reads as distinctly Ayayay. Wordmark is the
  // AYAYAY / MEXICAN EATERY lockup — wide aspect ratio.
  ayayay: {
    backgroundColor: '#F5A8A0',
    borderColor: 'rgba(15, 90, 77, 0.20)',
    primaryColor: '#0F5A4D',
    accentColor: '#D33C2A',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: 'Helvetica, "Helvetica Neue", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#0F5A4D',
    textSecondary: 'rgba(15, 90, 77, 0.62)',
    surfacePanel: '#F8B9B2',
    agentBubbleBg: '#FAEDC7',
    agentBubbleText: '#0F5A4D',
    userBubbleText: '#FAEDC7',
    sendButtonDisabledBg: 'rgba(15, 90, 77, 0.16)',
    wavePalette: 'ayayay',
    logoDisplayMode: 'wide',
  },

  // Trattoria — Modern Italian at 102-4501 Kingsway, Burnaby (Glowbal Group
  // Vancouver portfolio). Real brand identity is BRIGHT + AIRY: white canvas,
  // hand-drawn chartreuse lemon mark (one whole + one halved cross-section,
  // ~#D0CC2D), classical Italian typography (Italiana display + Montserrat
  // body — matches glowbalgroup.com/trattoria/events). Wide wordmark (574x263),
  // chartreuse pops on white. Black for text + secondary buttons.
  trattoria: {
    // Trattoria by Italian Kitchen — the real brand is DEEP FOREST GREEN canvas
    // with a white serif wordmark + chartreuse-lemon mark (not the cream/white
    // canvas it was mis-themed as). The white "TRATTORIA" wordmark only reads on
    // green, so the canvas IS green; chartreuse drives the buttons/accents.
    backgroundColor: '#143C28', // deep forest green (brand canvas)
    borderColor: 'rgba(208, 204, 45, 0.22)', // chartreuse hairline
    primaryColor: '#D0CC2D', // chartreuse — buttons + accents pop on green
    accentColor: '#B5B127', // darker chartreuse (hover/gradient)
    fontDisplay: '"Italiana", Georgia, "Times New Roman", serif',
    fontBody: '"Montserrat", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#F5F3E7', // warm cream/white
    textSecondary: 'rgba(245, 243, 231, 0.66)',
    surfacePanel: '#0F2E1F', // darker forest panel (distinct from canvas)
    agentBubbleBg: '#1B4A33', // mid-forest agent bubble
    agentBubbleText: '#F5F3E7',
    userBubbleText: '#143C28', // dark green text on the chartreuse user bubble
    sendButtonDisabledBg: 'rgba(245, 243, 231, 0.12)',
    // The COMPACT lemon emblem fills the circular avatar cleanly on /embed via
    // 'circle'. The full white wordmark is rendered on the /book page (wide) —
    // see book-client's trattoria override — where the green canvas makes it
    // read. The corner pill uses the lemon too (trattoria-pill-logo.png).
    logoDisplayMode: 'circle',
    logoObjectFit: 'contain',
    wavePalette: 'green', // green voice ribbon (Italian); flips to white when the customer speaks
  },

  // Matilda — "Unexpected Mexican cuisine" at 535 N Wells Street, Chicago.
  // Mexican-Peruvian fusion (same cuisine roots as Ayayay) but a totally
  // different brand identity: warm cream canvas + pure black accent. The
  // minimal "M" emblem with "UNEXPECTED MEXICAN CUISINE" circling around
  // is the venue's signature mark; black-on-cream is the only color story.
  // Hand-signed "with love, Matilda" tone on the homepage. The M emblem
  // is square (used as circle avatar inside the widget panel); the
  // wordmark (matilda-logo.png) is wide and used on the floating pill.
  matilda: {
    backgroundColor: '#F9F2E6',
    borderColor: 'rgba(0, 0, 0, 0.18)',
    primaryColor: '#000000',
    accentColor: '#000000',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: 'Helvetica, "Helvetica Neue", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#1A1A1A',
    textSecondary: 'rgba(0, 0, 0, 0.55)',
    surfacePanel: '#F2EBDA',
    agentBubbleBg: '#FFFFFF',
    agentBubbleText: '#1A1A1A',
    userBubbleText: '#F9F2E6',
    sendButtonDisabledBg: 'rgba(0, 0, 0, 0.12)',
    wavePalette: 'matilda',
    // M emblem renders as a circle avatar in the widget panel + /book.
    // logoObjectFit='contain' fits the round disk inside the circle
    // without cropping. logoCircleBackground='#000000' sits the cream-
    // tinted emblem on a black backplate so it reads on /book/matilda's
    // cream page bg (where the same cream-on-cream would be invisible).
    // The black circle echoes the venue's two-color brand identity (the
    // black footer / Submit button on the real events page).
    logoObjectFit: 'contain',
    logoCircleBackground: '#000000',
  },

  // Exodo Hospitality Group — parent group behind 5 Wells Street sister
  // venues. Brand sampled from exodohospitality.com/private-events: pure
  // black canvas + pure white text. Minimal / sharp / luxury. No
  // competing color. The widget surfaces (panel + pill) carry the EXODO
  // wordmark (white-on-transparent) and the agent routes leads to one of
  // 5 child venues (or 'no preference').
  exodo: {
    backgroundColor: '#000000',
    borderColor: 'rgba(255, 255, 255, 0.18)',
    primaryColor: '#FFFFFF',
    accentColor: '#FFFFFF',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: 'Helvetica, "Helvetica Neue", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.62)',
    surfacePanel: '#0A0A0A',
    agentBubbleBg: '#1C1C1C',
    agentBubbleText: '#FFFFFF',
    userBubbleText: '#000000',
    sendButtonDisabledBg: 'rgba(255, 255, 255, 0.12)',
    wavePalette: 'exodo',
    // EXODO wordmark is wide (240x135 after resize from 4269x2400 source) —
    // render with logoDisplayMode='wide' so it spans cleanly instead of
    // getting cropped inside a circle.
    logoDisplayMode: 'wide',
  },

  // The Delancey NYC — three-level club at 168 Delancey St by the Williamsburg
  // Bridge. Brand sampled from thedelancey.com: black canvas + cream (#F2EBD8)
  // accent. Decorative banner-style "THE DELANCEY NYC" logo is monochrome
  // line-art (cream-tinted in widget-logo.png for legibility on the black
  // panel). NYC nightclub identity — black + cream, no competing color.
  delancey: {
    backgroundColor: '#000000',
    borderColor: 'rgba(242, 235, 216, 0.20)',
    primaryColor: '#F2EBD8',
    accentColor: '#F2EBD8',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: 'Helvetica, "Helvetica Neue", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#F2EBD8',
    textSecondary: 'rgba(242, 235, 216, 0.62)',
    surfacePanel: '#0A0A0A',
    agentBubbleBg: '#1C1C1C',
    agentBubbleText: '#F2EBD8',
    userBubbleText: '#000000',
    sendButtonDisabledBg: 'rgba(242, 235, 216, 0.12)',
    wavePalette: 'delancey',
    // Banner logo is monochrome line-art on transparent; logoObjectFit
    // 'contain' shows the full decorative banner inside the 96x96 circle.
    logoObjectFit: 'contain',
  },

  // Coast — Glowbal Group's flagship seafood restaurant (1054 Alberni St,
  // Vancouver). Deep ocean-teal canvas + sea-foam cream text + bright sea-glass
  // accent. FIRST factory-minted venue (scripts/widget-factory). Palette is a
  // first pass authored from the spec; the data-driven theme (plan Phase 2)
  // will let the dashboard tune it without a code edit.
  coast: {
    backgroundColor: '#0E2A33',
    borderColor: 'rgba(234, 241, 242, 0.16)',
    primaryColor: '#EAF1F2',
    accentColor: '#5FB6C4',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#EAF1F2',
    textSecondary: 'rgba(234, 241, 242, 0.62)',
    surfacePanel: '#13323C',
    // Coast's logo is a WIDE wordmark — render it as a horizontal lockup
    // (objectFit:contain) instead of cropping it into the circular avatar.
    logoDisplayMode: 'wide',
    wavePalette: 'cyan', // sea-glass voice ribbon, not default gold
    agentBubbleBg: '#EAF1F2',
    agentBubbleText: '#0E2A33',
    userBubbleText: '#0E2A33',
    sendButtonDisabledBg: 'rgba(234, 241, 242, 0.12)',
  },

  // ── Glowbal Restaurant Group (factory-minted, R-Glowbal) ──────────────────
  // Accents are logo-derived (dominant saturated pixel); panel direction chosen
  // per the theme-review board. All use logoDisplayMode:'wide' (wordmark logos).

  // Glowbal flagship — monochrome wordmark, so accent is the group's champagne
  // gold on a warm near-black. Polished, social-host register.
  // Glowbal — the group's real brand is GREY + MAGENTA-PURPLE (verified from
  // glowbalgroup.com/private-dining.html: dark-grey sections, #8B2A8C booking
  // buttons). Not the earlier champagne-gold guess (its logo is monochrome so
  // there was no extractable accent — the site is the source of truth).
  glowbal: {
    backgroundColor: '#2E2C2E',
    borderColor: 'rgba(139, 42, 140, 0.30)',
    primaryColor: '#F2EEF2',
    accentColor: '#8B2A8C',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#F2EEF2',
    textSecondary: 'rgba(242, 238, 242, 0.62)',
    surfacePanel: '#383538',
    logoDisplayMode: 'wide',
    wavePalette: 'glowbal', // voice-mode ribbon = brand magenta-purple, not default gold
    agentBubbleBg: '#F2EEF2',
    agentBubbleText: '#2E2C2E',
    userBubbleText: '#FFFFFF',
    sendButtonDisabledBg: 'rgba(242, 238, 242, 0.12)',
  },

  // Italian Kitchen — navy logo (#184878) on a warm cream panel (Italian warmth;
  // the navy wordmark reads best on light, not dark). The one LIGHT-panel venue.
  'italian-kitchen': {
    backgroundColor: '#FBF6EC',
    borderColor: 'rgba(24, 72, 120, 0.18)',
    primaryColor: '#184878',
    accentColor: '#184878',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#1C2A3A',
    textSecondary: 'rgba(28, 42, 58, 0.62)',
    surfacePanel: '#FFFFFF',
    logoDisplayMode: 'wide',
    wavePalette: 'blue', // navy voice ribbon to match the logo/accent; flips to white when the customer speaks
    // The only light-panel Glowbal venue. A white listening-flip would be
    // invisible on the white panel, so the voice-stage frame gets a deep-navy
    // backdrop — the navy ribbon AND the white flip both read against it,
    // while the rest of the widget keeps its warm cream Italian look.
    waveStageBg: '#0C1E33',
    agentBubbleBg: '#184878',
    agentBubbleText: '#FBF6EC',
    userBubbleText: '#FFFFFF',
    sendButtonDisabledBg: 'rgba(24, 72, 120, 0.14)',
  },

  // Five Sails — Pacific-NW fine dining at Canada Place. White wordmark on a
  // deep slate-blue (mountains-meet-ocean); cool seafoam accent. Most refined.
  'five-sails': {
    backgroundColor: '#16242E',
    borderColor: 'rgba(214, 226, 230, 0.16)',
    primaryColor: '#EDF2F4',
    accentColor: '#8FB6BE',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#EDF2F4',
    textSecondary: 'rgba(237, 242, 244, 0.60)',
    surfacePanel: '#1E303B',
    logoDisplayMode: 'wide',
    wavePalette: 'cyan', // cool seafoam voice ribbon (Pacific NW), not gold
    agentBubbleBg: '#EDF2F4',
    agentBubbleText: '#16242E',
    userBubbleText: '#16242E',
    sendButtonDisabledBg: 'rgba(237, 242, 244, 0.12)',
  },

  // Black + Blue steakhouse (shared by Vancouver + Toronto). Gold monogram
  // (#907848) glows on near-black — bold, upscale steakhouse register.
  'black-and-blue': {
    backgroundColor: '#0E0E10',
    borderColor: 'rgba(178, 148, 88, 0.20)',
    primaryColor: '#F2EAD9',
    accentColor: '#B29458',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#F2EAD9',
    textSecondary: 'rgba(242, 234, 217, 0.60)',
    surfacePanel: '#16161A',
    logoDisplayMode: 'wide',
    wavePalette: 'blackGold', // BLACK + GOLD ribbon; flips to white when the customer speaks (own palette, not The Delancey NYC's)
    agentBubbleBg: '#F2EAD9',
    agentBubbleText: '#0E0E10',
    userBubbleText: '#0E0E10',
    sendButtonDisabledBg: 'rgba(242, 234, 217, 0.12)',
  },

  // The Roof — Black + Blue's rooftop. Same gold mark, a touch lighter/airier
  // panel than the steakhouse below it to read "open-air".
  'the-roof': {
    backgroundColor: '#15171B',
    borderColor: 'rgba(178, 148, 88, 0.18)',
    primaryColor: '#F2EAD9',
    accentColor: '#B29458',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#F2EAD9',
    textSecondary: 'rgba(242, 234, 217, 0.60)',
    surfacePanel: '#1E2127',
    logoDisplayMode: 'wide',
    wavePalette: 'blackGold', // BLACK + GOLD ribbon; flips to white when the customer speaks (shares Black + Blue's brand)
    agentBubbleBg: '#F2EAD9',
    agentBubbleText: '#15171B',
    userBubbleText: '#15171B',
    sendButtonDisabledBg: 'rgba(242, 234, 217, 0.12)',
  },

  // Riley's Fish & Steak (shared by Vancouver + Toronto). Warm amber wordmark
  // (#A87848) on near-black — friendly, spirited, casual-upscale.
  rileys: {
    backgroundColor: '#121010',
    borderColor: 'rgba(168, 120, 72, 0.20)',
    primaryColor: '#F1E7DA',
    accentColor: '#C08A4E',
    fontDisplay: '"Instrument Serif", Georgia, "Times New Roman", serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#F1E7DA',
    textSecondary: 'rgba(241, 231, 218, 0.60)',
    surfacePanel: '#1A1614',
    logoDisplayMode: 'wide',
    wavePalette: 'orange', // warm amber voice ribbon to match Riley's accent
    agentBubbleBg: '#F1E7DA',
    agentBubbleText: '#121010',
    userBubbleText: '#121010',
    sendButtonDisabledBg: 'rgba(241, 231, 218, 0.12)',
  },

  // Baccalà — Venetian seafood + wine, Bermondsey London. White + teal: a deep
  // sage-teal panel (drawn from the brand's grey-teal logo ground) with a white
  // wordmark + white text, and a white→teal voice ribbon. Clean modern sans to
  // match the geometric wordmark.
  baccala: {
    backgroundColor: '#2E3F3B',
    borderColor: 'rgba(255, 255, 255, 0.16)',
    primaryColor: '#FFFFFF',
    accentColor: '#5FD3C0',
    fontDisplay: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    fontBody: '"General Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.62)',
    surfacePanel: '#374A45',
    logoDisplayMode: 'wide',
    wavePalette: 'baccala', // white → sage/teal ribbon
    agentBubbleBg: '#EDEFEE',
    agentBubbleText: '#243330',
    userBubbleText: '#243330',
    sendButtonDisabledBg: 'rgba(255, 255, 255, 0.12)',
  },
};

export function isBrandPreset(value: string | null | undefined): value is BrandPreset {
  return (
    value === 'default' || value === 'akiro' || value === 'ottavio' ||
    value === 'membersonly' || value === 'boxcar' || value === 'andson' ||
    value === 'mala' || value === 'clandestino' || value === 'kayao' ||
    value === 'ayayay' || value === 'matilda' || value === 'exodo' ||
    value === 'delancey' || value === 'trattoria' || value === 'coast' ||
    value === 'glowbal' || value === 'italian-kitchen' || value === 'five-sails' ||
    value === 'black-and-blue' || value === 'the-roof' || value === 'rileys' ||
    value === 'baccala' || value === 'lebuffet'
  );
}

/**
 * SINGLE SOURCE OF TRUTH for a venue's widget theme. EVERY customer-facing widget surface
 * (/embed, /book, /demo, /venue) MUST resolve its theme through this — do NOT re-implement
 * the preset-vs-tokens logic per surface (that drift is exactly why a venue's brand only
 * themed wherever someone remembered the merge).
 *
 * - A REGISTERED preset → its BRAND_PRESETS entry verbatim (byte-identical render).
 * - The default/unregistered preset → the default theme with the venue's data-driven
 *   brandTokens (catering_config.widgetBrandTokens, written by the widget factory's
 *   buildCateringConfig from spec.brand.tokens) merged on top. So a venue self-themes ALL
 *   surfaces from ONE input — no registry edit, no per-surface code. This is the contract
 *   the factory relies on: set brand.tokens once → every surface is themed.
 */
export function resolveBrandTheme(
  preset: BrandPreset,
  brandTokens?: Record<string, unknown> | null,
): Partial<CateringWidgetTheme> {
  if (preset === 'default' && brandTokens && Object.keys(brandTokens).length > 0) {
    return { ...BRAND_PRESETS.default, ...(brandTokens as Partial<CateringWidgetTheme>) };
  }
  return BRAND_PRESETS[preset];
}

// Trigger pill config for the corner-placement floating widget. Values were
// hardcoded in each demo page (apps/web/src/app/demo/<venue>/demo-client.tsx);
// hoisting to a shared map so /embed/<id>?placement=corner can render the same
// trigger as the demo without re-deriving constants. New venues get an entry
// here and the corner-pill snippet starts working for them.
//
// Kept as a separate map (not folded into BRAND_PRESETS) to avoid churning
// the 6+ existing call sites that already pass BRAND_PRESETS[preset] as the
// CateringWidgetTheme directly. Refactor to a single nested config in v2.
export interface TriggerConfig {
  /** Optional: when omitted, the call site falls back to the venue's live
   * DB logo (cateringConfig.widgetLogoUrl). Use this for venues whose logo is
   * uploaded to storage (factory-minted) rather than committed to /public —
   * the preset then carries brand COLORS only (env-agnostic), the logo IMAGE
   * stays data-driven (env-specific). */
  logoUrl?: string;
  background: string;
  label: string;
  textColor: string;
  ariaLabel: string;
}
export interface CopyConfig {
  title: string;
  description: string;
  /**
   * Optional per-venue override for the idle-state "Chat" button label. When
   * set, the call site passes it as `chatLabelOverride` to the widget, which
   * uses it in place of the global `t('widget.idle.chat')` label. Undefined for
   * every venue except Members Only — so other venues keep the localized
   * default. Used to surface a venue-specific CTA ("Chat with the … Team").
   */
  chatLabel?: string;
}

export const BRAND_TRIGGER_CONFIG: Record<BrandPreset, TriggerConfig | undefined> = {
  default: undefined,
  akiro: {
    // Akiro pill uses the wordmark (the AKIRO kana/Latin lockup), not the
    // bowl. Bowl is the /demo/akiro-events expanded panel header logo
    // (AKIRO_BOWL_LOGO = '/akiro-widget-logo.png'); the wordmark
    // (AKIRO_WORDMARK = '/akiro-wordmark.webp') is what the bespoke
    // FloatingWidget passes as triggerLogoUrl. Earlier this entry
    // pointed at '/akiro-bowl-icon.png' which doesn't exist in
    // apps/web/public/ — broken image in any /embed/<id>?placement=corner
    // call site that resolved this preset.
    logoUrl: '/akiro-wordmark.webp',
    background: '#0A0A0A',
    label: 'Plan a private event',
    textColor: '#F5F0E6',
    ariaLabel: 'Plan a private event at Akiro',
  },
  ottavio: {
    // External URL — same source the bespoke /demo/ottavio-events page uses
    // (no local /ottavio-widget-logo.png exists in apps/web/public/). If the
    // remote ever 404s, copy this asset locally and update both call sites.
    logoUrl: 'https://ottavio.ca/wp-content/uploads/2016/10/ottavio.png',
    background: '#FBF7EF',
    label: 'Planifier un événement · Plan an event',
    textColor: '#1A0505',
    ariaLabel: 'Plan a private event at Ottavio',
  },
  lebuffet: {
    logoUrl: 'https://lebuffetdescontinents.ca/wp-content/themes/PUBinteractive2/img/bdc_logo_blanc.png',
    background: '#E7791C',
    label: 'Planifier un événement · Plan a group event',
    textColor: '#FFFFFF',
    ariaLabel: 'Plan a group event at Le Buffet des Continents',
  },
  membersonly: {
    logoUrl: '/membersonly-widget-logo.png',
    background: '#000000',
    label: 'Chat with the Members Only Team',
    textColor: '#F5F0E6',
    ariaLabel: 'Plan a private event at Members Only',
  },
  boxcar: {
    logoUrl: '/boxcar-widget-logo.png',
    background: '#000000',
    label: 'Plan a private event',
    textColor: '#F7F7F7',
    ariaLabel: 'Plan a private event at Boxcar Social',
  },
  andson: {
    // Hand-crafted SVG wordmark: gold serif "& SON / STEAKEASY" on
    // transparent so it reads cleanly on the dark pill background.
    // Mirror of the gold-foil-on-cream-paper wordmark from the venue's
    // physical brand identity, simplified for legibility at pill scale.
    logoUrl: '/andson-wordmark.svg',
    background: '#0A0A0A',
    label: 'Plan a private event',
    textColor: '#C9A55C',
    ariaLabel: 'Plan a private event at & Son Steakeasy',
  },
  mala: {
    // Pink circle "SERVING JOY SINCE 2015" logo on the source page's purple
    // CTA color (#67287F). Yellow #FFE71E text on purple gives the same
    // visual register as the malaproject.com hero "book now!" pill. Stands
    // out against the bright yellow canvas of the bespoke demo page.
    // R42.1 — bilingual EN/中文 to mirror the rest of malaproject.com.
    logoUrl: '/mala-circle-logo.jpg',
    background: '#67287F',
    label: 'Plan a private event · 私人活动预订',
    textColor: '#FFE71E',
    ariaLabel: 'Plan a private event at MáLà Project · 在麻辣计划策划私人活动',
  },
  clandestino: {
    // Wordmark is red on transparent. Needs a BLACK pill background to read
    // (red-on-red is invisible — caught in staging review). Matches the /book
    // page where the wordmark sits on pure black. The "Plan a private event"
    // label flips to brand red on black for visual hierarchy.
    logoUrl: '/clandestino-wordmark.png',
    background: '#000000',
    label: 'Plan a private event',
    textColor: '#FFFFFF',
    ariaLabel: 'Plan a private event at Clandestino',
  },
  kayao: {
    // kayao fish + "kayao" lockup. The fish is bright coral on transparent;
    // pill background is deep teal so the coral logo pops without competing
    // with itself. Cream text label.
    logoUrl: '/kayao-wordmark.svg',
    background: '#013334',
    label: 'Plan a private event',
    textColor: '#E8E4D8',
    ariaLabel: 'Plan a private event at Kayao',
  },
  ayayay: {
    // AYAYAY / MEXICAN EATERY wordmark — cream-on-transparent. Pill bg
    // flips to the venue's deep forest green so the cream wordmark + label
    // read cleanly. The pink-canvas brand identity is reserved for the page
    // body; the pill itself remains green for legibility at small scale.
    logoUrl: '/ayayay-wordmark.png',
    background: '#0F5A4D',
    label: 'Plan a private event',
    textColor: '#FAEDC7',
    ariaLabel: 'Plan a private event at Ayayay',
  },
  trattoria: {
    // Compact chartreuse-lemon mark on the GREEN brand pill (the wide white
    // wordmark would shrink to an illegible strip in the 44px trigger circle —
    // the lemon fills it cleanly). Cream label reads on forest green.
    logoUrl: '/trattoria-pill-logo.png',
    background: '#143C28',
    label: 'Plan a private event',
    textColor: '#F5F3E7',
    ariaLabel: 'Plan a private event at Trattoria',
  },
  matilda: {
    // Round M emblem ("UNEXPECTED MEXICAN CUISINE / MATILDA"), tinted cream
    // for legibility on the black pill background. The wide wordmark
    // (/matilda-wordmark.png) would squish into the 44px trigger circle as
    // an unreadable strip — the round emblem fills the circle cleanly and
    // reads at any scale. Cream-on-black mirrors the venue's only-two-
    // colors brand identity.
    logoUrl: '/matilda-pill-logo.png',
    background: '#000000',
    label: 'Plan a private event',
    textColor: '#F9F2E6',
    ariaLabel: 'Plan a private event at Matilda',
  },
  exodo: {
    // Full EXODO wordmark — shown on /book/exodo + bespoke widget panel
    // via logoDisplayMode='wide' (renders as a horizontal lockup spanning
    // 220px wide). The compact X-mark (/exodo-widget-logo.png) is used
    // only on the floating pill — passed explicitly via triggerLogoUrl
    // from demo-client.tsx since the pill's 44px circle can't fit a
    // wide wordmark cleanly.
    logoUrl: '/exodo-wordmark.png',
    background: '#000000',
    label: 'Book an event',
    textColor: '#FFFFFF',
    ariaLabel: 'Book a private event with Exodo Hospitality Group',
  },
  delancey: {
    // Decorative "THE DELANCEY NYC" banner logo (cream on transparent for
    // the widget surfaces' black backgrounds). Banner is square-ish so it
    // fits cleanly inside both the 44px pill circle and the larger panel
    // avatar via logoObjectFit='contain'.
    logoUrl: '/delancey-widget-logo.png',
    background: '#000000',
    label: 'Plan a private event',
    textColor: '#F2EBD8',
    ariaLabel: 'Plan a private event at The Delancey NYC',
  },
  coast: {
    // Brand COLORS only — no static logoUrl. Coast's transparent wordmark is
    // uploaded to Supabase storage (factory ingest) and served from the live
    // DB (cateringConfig.widgetLogoUrl); the embed/demo call sites fall back to
    // that when trigger.logoUrl is absent. Deep ocean-teal pill so the blue
    // wordmark + cream label read cleanly (mirrors the coast BRAND_PRESET).
    background: '#0E2A33',
    label: 'Plan a private event',
    textColor: '#EAF1F2',
    ariaLabel: 'Plan a private event at Coast',
  },
  // Glowbal group (factory-minted) — colors only; the DB widgetLogoUrl supplies
  // the wordmark on the pill. Pill bg + text mirror each venue's BRAND_PRESET.
  glowbal: {
    background: '#2E2C2E',
    label: 'Plan a private event',
    textColor: '#F2EEF2',
    ariaLabel: 'Plan a private event at Glowbal',
  },
  'italian-kitchen': {
    background: '#FBF6EC',
    label: 'Plan a private event',
    textColor: '#184878',
    ariaLabel: 'Plan a private event at Italian Kitchen',
  },
  'five-sails': {
    background: '#16242E',
    label: 'Plan a private event',
    textColor: '#EDF2F4',
    ariaLabel: 'Plan a private event at Five Sails',
  },
  'black-and-blue': {
    background: '#0E0E10',
    label: 'Plan a private event',
    textColor: '#F2EAD9',
    ariaLabel: 'Plan a private event at Black + Blue',
  },
  'the-roof': {
    background: '#15171B',
    label: 'Plan a rooftop event',
    textColor: '#F2EAD9',
    ariaLabel: 'Plan a private event at The Roof',
  },
  rileys: {
    background: '#121010',
    label: 'Plan a private event',
    textColor: '#F1E7DA',
    ariaLabel: "Plan a private event at Riley's",
  },
  baccala: {
    background: '#2E3F3B',
    label: 'Plan an event',
    textColor: '#FFFFFF',
    ariaLabel: 'Plan a private event at Baccalà',
  },
};

export const BRAND_COPY: Record<BrandPreset, CopyConfig | undefined> = {
  default: undefined,
  akiro: {
    title: 'Plan a private event at Akiro',
    description: 'Call or chat with our AI hostess — two-minute intake, the team follows up.',
  },
  ottavio: {
    title: 'Planifier un événement chez Ottavio',
    description: 'Appelez ou écrivez à notre hôtesse IA — une intake de deux minutes, l’équipe vous rappellera.',
  },
  lebuffet: {
    title: 'Planifier un événement de groupe · Le Buffet des Continents',
    description: 'Appelez ou écrivez à notre hôtesse IA pour planifier un événement de groupe — l’équipe vous rappellera. / Call or chat to plan a group event.',
  },
  membersonly: {
    title: 'Welcome to the Club. Everyone’s a Member. 🥂',
    description: 'Call or text to book an event or ask the team about assistance for a reservation.',
  },
  boxcar: {
    title: 'Plan a private event at Boxcar Social',
    description: 'Pick your venue, then call or chat — our assistant helps capture event details and the team follows up.',
  },
  andson: {
    title: 'Plan a private event at & Son Steakeasy',
    description: 'Chat with our private events host — two-minute intake, the team follows up.',
  },
  mala: {
    title: 'Plan a private event at MáLà · 私人活动预订',
    description: 'Chat with our events host · 与活动主持人聊天 — pick a location, share your party size, two-minute intake, the team follows up.',
  },
  clandestino: {
    title: 'Plan a private event at Clandestino',
    description: 'Chat with our events host — two-minute intake, the team follows up. It\'s where those who know — know.',
  },
  kayao: {
    title: 'Plan a private event at Kayao',
    description: 'Chat with our events host — two-minute intake, the team follows up. A culinary oasis on the vibrant streets of Chicago.',
  },
  ayayay: {
    title: 'Plan a private event at Ayayay',
    description: 'Chat with our events host — two-minute intake, the team follows up. Real comida & fiesta, my love.',
  },
  trattoria: {
    title: 'Plan a private event at Trattoria',
    description: 'Chat with our events host — two-minute intake, the team follows up. Authentic Italian, made for sharing.',
  },
  matilda: {
    title: 'Plan a private event at Matilda',
    description: 'Chat with our events host — two-minute intake, the team follows up. Unexpected Mexican cuisine — with love.',
  },
  exodo: {
    title: 'Book an event with Exodo',
    description: 'Chat with our group host — pick from Akiro, Ayayay, Clandestino, Kayao, or Matilda, or let the team match you to the right venue.',
  },
  delancey: {
    title: 'Plan a private event at The Delancey',
    description: 'Chat with our events host — pick the Rooftop Garden, Main Floor, or Downstairs Lounge, or let the team suggest the right space for your night.',
  },
  coast: {
    title: 'Plan a private event at Coast',
    description: 'Chat with our events host — two-minute intake, the team follows up. West Coast seafood, elevated.',
  },
  glowbal: {
    title: 'Plan an event at Glowbal',
    description: 'Chat with our events host — two-minute intake, the team follows up. Global flavours, Vancouver soul.',
  },
  'italian-kitchen': {
    title: 'Plan an event at Italian Kitchen',
    description: 'Chat with our events host — two-minute intake, the team follows up. Family-style Italian, downtown Vancouver.',
  },
  'five-sails': {
    title: 'Plan an event at Five Sails',
    description: 'Chat with our events host — two-minute intake, the team follows up. Refined Pacific Northwest dining at Canada Place.',
  },
  'black-and-blue': {
    title: 'Plan an event at Black + Blue',
    description: 'Chat with our events host — two-minute intake, the team follows up. A steakhouse, reimagined.',
  },
  'the-roof': {
    title: 'Plan a rooftop event at The Roof',
    description: 'Chat with our events host — two-minute intake, the team follows up. Open-air rooftop dining above Black + Blue.',
  },
  rileys: {
    title: "Plan an event at Riley's",
    description: 'Chat with our events host — two-minute intake, the team follows up. Fish, steak, and good times.',
  },
  baccala: {
    title: 'Plan an event at Baccalà',
    description: 'Chat with our events host — two-minute intake, the team follows up. Venetian-inspired seafood & wine in Bermondsey, London.',
  },
};
